/**
 * API Client pour FAQ - Foire Aux Questions
 * Partagé BackOffice / FrontOffice (utilisé aussi par le chatbot guide)
 */

import { api } from '../services/api';
import type { UserRole } from '../types/auth';

export interface FAQCategory {
  categoryId: string;
  name: string;
  description?: string;
  order: number;
  createdAt: string;
  updatedAt: string;
}

export interface FAQItem {
  faqId: string;
  categoryId: string;
  categoryName: string;
  question: string;
  answer: string;
  order: number;
  isPublished: boolean;
  /** Rôles pouvant FAIRE l'action décrite (absent/vide = tous les rôles) */
  allowedRoles?: UserRole[];
  views: number;
  helpful: number;
  notHelpful: number;
  createdBy: string;
  createdByName: string;
  createdAt: string;
  updatedAt: string;
  updatedBy?: string;
  updatedByName?: string;
}

export interface CreateFAQCategoryInput {
  name: string;
  description?: string;
  order?: number;
}

export interface UpdateFAQCategoryInput {
  name?: string;
  description?: string;
  order?: number;
}

export interface CreateFAQItemInput {
  categoryId: string;
  question: string;
  answer: string;
  order?: number;
  isPublished?: boolean;
  allowedRoles?: UserRole[];
}

export interface UpdateFAQItemInput {
  categoryId?: string;
  question?: string;
  answer?: string;
  order?: number;
  isPublished?: boolean;
  allowedRoles?: UserRole[];
}

export interface SearchFAQInput {
  query: string;
  categoryId?: string;
}

export interface FAQStats {
  views: number;
  helpful: number;
  notHelpful: number;
  helpfulPercentage: number;
}

export const faqApi = {
  // ====================================
  // CATÉGORIES
  // ====================================

  /**
   * Lister toutes les catégories
   */
  getCategories: () => api.get<{ categories: FAQCategory[] }>('/api/faq/categories'),

  /**
   * Créer une catégorie (admin)
   */
  createCategory: (data: CreateFAQCategoryInput) =>
    api.post<{ category: FAQCategory }>('/api/faq/categories', data),

  /**
   * Modifier une catégorie (admin)
   */
  updateCategory: (categoryId: string, data: UpdateFAQCategoryInput) =>
    api.put<{ category: FAQCategory }>(`/api/faq/categories/${categoryId}`, data),

  /**
   * Supprimer une catégorie (admin)
   */
  deleteCategory: (categoryId: string) => api.delete(`/api/faq/categories/${categoryId}`),

  // ====================================
  // ITEMS FAQ
  // ====================================

  /**
   * Lister les items FAQ
   * @param categoryId - Filtrer par catégorie (optionnel)
   */
  getItems: (categoryId?: string) => {
    const params = categoryId ? `?categoryId=${categoryId}` : '';
    return api.get<{ items: FAQItem[] }>(`/api/faq/items${params}`);
  },

  /**
   * Récupérer un item FAQ par ID
   */
  getItem: (faqId: string) => api.get<{ item: FAQItem }>(`/api/faq/items/${faqId}`),

  /**
   * Créer un item FAQ (admin)
   */
  createItem: (data: CreateFAQItemInput) =>
    api.post<{ item: FAQItem }>('/api/faq/items', data),

  /**
   * Modifier un item FAQ (admin)
   */
  updateItem: (faqId: string, data: UpdateFAQItemInput) =>
    api.put<{ item: FAQItem }>(`/api/faq/items/${faqId}`, data),

  /**
   * Supprimer un item FAQ (admin)
   */
  deleteItem: (faqId: string) => api.delete(`/api/faq/items/${faqId}`),

  /**
   * Incrémenter le compteur de vues
   */
  incrementView: (faqId: string) => api.post(`/api/faq/items/${faqId}/view`, {}),

  /**
   * Enregistrer un feedback
   */
  recordFeedback: (faqId: string, helpful: boolean) =>
    api.post(`/api/faq/items/${faqId}/feedback`, { helpful }),

  /**
   * Obtenir les statistiques d'une question
   */
  getStats: (faqId: string) => api.get<{ stats: FAQStats }>(`/api/faq/items/${faqId}/stats`),

  // ====================================
  // RECHERCHE
  // ====================================

  /**
   * Rechercher dans les FAQ
   */
  search: (data: SearchFAQInput) => api.post<{ items: FAQItem[] }>('/api/faq/search', data),
};
